import React, { useState, useRef, useEffect } from 'react';
import { generateAiGif } from '../utils/aiGifApi';
import './AiGifModal.css';

const STATUS_LABEL = {
  queued: 'Queued…',
  processing: 'Generating…',
  done: 'Finishing…',
};

/**
 * Inline AI generation UI, rendered inside the GIF picker's "Generate" tab.
 * Prompt -> animated WebP, with a preview step and a single allowed regenerate.
 * On send, hands the hosted URL to onSelectGif() (same path as a Tenor GIF).
 */
function AiGifPanel({ onSelectGif, onClose }) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [regensLeft, setRegensLeft] = useState(1);
  const inputRef = useRef(null);
  const tickRef = useRef(null);
  const startRef = useRef(0);
  const phaseRef = useRef('queued');

  useEffect(() => {
    inputRef.current?.focus();
    return () => clearInterval(tickRef.current);
  }, []);

  const startProgress = () => {
    startRef.current = Date.now();
    phaseRef.current = 'queued';
    setProgress(0);
    clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const ceiling = phaseRef.current === 'queued' ? 25 : 95;
      const tau = phaseRef.current === 'queued' ? 6000 : 9000;
      const pct = ceiling * (1 - Math.exp(-elapsed / tau));
      setProgress((p) => Math.max(p, Math.min(ceiling, pct)));
    }, 120);
  };

  const stopProgress = (final) => {
    clearInterval(tickRef.current);
    if (typeof final === 'number') setProgress(final);
  };

  const runGeneration = async () => {
    const p = prompt.trim();
    if (!p || busy) return;
    setBusy(true);
    setError('');
    setStatus('queued');
    startProgress();
    try {
      const url = await generateAiGif(p, (s) => {
        setStatus(s);
        phaseRef.current = s;
      });
      stopProgress(100);
      setPreviewUrl(url);
    } catch (e) {
      stopProgress(0);
      setError(e?.message || 'Failed to generate. Try again.');
    } finally {
      setBusy(false);
      setStatus('');
    }
  };

  const handleRegenerate = () => {
    if (regensLeft <= 0 || busy) return;
    setRegensLeft((n) => n - 1);
    setPreviewUrl('');
    runGeneration();
  };

  const handleSend = () => {
    if (!previewUrl) return;
    onSelectGif(previewUrl);
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!previewUrl) runGeneration();
    }
  };

  return (
    <div className="aigif-body aigif-body--panel">
      <textarea
        ref={inputRef}
        className="aigif-input"
        placeholder="Describe your GIF…"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={onKeyDown}
        maxLength={500}
        disabled={busy}
        rows={2}
      />

      {error && <div className="aigif-error">{error}</div>}

      {busy && (
        <div className="aigif-progress">
          <div className="aigif-progress-head">
            <span>{STATUS_LABEL[status] || 'Generating…'}</span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="aigif-progress-track">
            <div className="aigif-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      {!busy && previewUrl && (
        <div className="aigif-preview">
          <img src={previewUrl} alt="Generated preview" />
        </div>
      )}

      {!previewUrl && !busy && (
        <button
          className="aigif-generate-btn"
          onClick={runGeneration}
          disabled={!prompt.trim()}
        >
          <span className="material-symbols-outlined">animation</span>
          Generate
        </button>
      )}

      {previewUrl && !busy && (
        <div className="aigif-actions">
          {regensLeft > 0 && (
            <button className="aigif-secondary-btn" onClick={handleRegenerate}>
              <span className="material-symbols-outlined">refresh</span>
              Regenerate
            </button>
          )}
          <button className="aigif-generate-btn aigif-send-btn" onClick={handleSend}>
            <span className="material-symbols-outlined">send</span>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

export default AiGifPanel;
